import { InboxNotification } from '@/apis/business/notifications/types';
import {
  useAgencyNotificationMutations,
  useAgencyNotifications,
  useAgencyNotificationsUnreadCount
} from '@/apis/agency/notifications/hook';
import {
  useBusinessNotificationMutations,
  useBusinessNotifications,
  useBusinessNotificationsUnreadCount
} from '@/apis/business/notifications/hook';
import { useBusiness } from '@/providers/context/BusinessProvider';
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Menu,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import React, { useEffect, useRef, useState } from 'react';
import { FiBell } from 'react-icons/fi';
import { NotificationRowIcon } from './notificationVisuals';
import ChatBotIcon from '@/assets/icons/ChatBot';
import { FaRegUser } from 'react-icons/fa6';

type Scope = 'business' | 'agency';

export default function NotificationBell({ scope }: { scope: Scope }) {
  if (scope === 'agency') {
    return <AgencyNotificationBellInner />;
  }
  return <BusinessNotificationBellInner />;
}

function BusinessNotificationBellInner() {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('md'));
  const { selectedBusiness } = useBusiness();
  const enabled = Boolean(selectedBusiness);
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchor);
  const [page, setPage] = useState(1);
  const [notificationList, setNotificationList] = useState<InboxNotification[]>([]);
  const isFetchingRef = useRef(false);
  const perPage = 25;

  const {
    data: listRes,
    status: listStatus,
    isFetching,
    refetch
  } = useBusinessNotifications(selectedBusiness, { per_page: perPage, page }, { enabled: enabled && menuOpen });
  const { data: unreadRes } = useBusinessNotificationsUnreadCount(selectedBusiness, { enabled });
  const { markRead, markAllRead } = useBusinessNotificationMutations(selectedBusiness);

  useEffect(() => {
    if (listRes?.data) {
      setNotificationList((prev) => {
        if (page === 1) {
          return listRes.data;
        }

        const merged = new Map(prev.map((n) => [n.id, n]));
        listRes.data.forEach((notification) => merged.set(notification.id, notification));
        return Array.from(merged.values());
      });
      isFetchingRef.current = false;
    }
  }, [listRes, page]);

  useEffect(() => {
    if (menuOpen) {
      void refetch();
      return;
    }

    if (!menuOpen) {
      setPage(1);
      isFetchingRef.current = false;
    }
  }, [menuOpen, refetch]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const nearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 50;
    const totalPages = (listRes?.meta?.total_pages as number) || 1;
    const hasMore = page < totalPages;

    if (nearBottom && hasMore && !isFetchingRef.current) {
      isFetchingRef.current = true;
      setPage((prev) => prev + 1);
    }
  };

  return (
    <NotificationBellShell
      isSm={isSm}
      enabled={enabled}
      anchor={anchor}
      setAnchor={setAnchor}
      menuOpen={menuOpen}
      unreadCount={unreadRes?.unread_count ?? 0}
      items={notificationList}
      listPending={listStatus === 'pending' && page === 1}
      isFetchingMore={isFetching}
      markRead={markRead}
      onMarkRead={(id) => {
        setNotificationList((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
      }}
      onMarkAllRead={() => {
        setNotificationList((prev) => prev.map((n) => ({ ...n, read: true })));
      }}
      markAllRead={markAllRead}
      onScroll={handleScroll}
    />
  );
}

function AgencyNotificationBellInner() {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('md'));
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchor);
  const [page, setPage] = useState(1);
  const [notificationList, setNotificationList] = useState<InboxNotification[]>([]);
  const isFetchingRef = useRef(false);
  const perPage = 15;

  const {
    data: listRes,
    status: listStatus,
    isFetching,
    refetch
  } = useAgencyNotifications({ per_page: perPage, page }, { enabled: menuOpen });
  const { data: unreadRes } = useAgencyNotificationsUnreadCount();
  const { markRead, markAllRead } = useAgencyNotificationMutations();

  useEffect(() => {
    if (listRes?.data) {
      setNotificationList((prev) => {
        if (page === 1) {
          return listRes.data;
        }

        const merged = new Map(prev.map((n) => [n.id, n]));
        listRes.data.forEach((notification) => merged.set(notification.id, notification));
        return Array.from(merged.values());
      });
      isFetchingRef.current = false;
    }
  }, [listRes, page]);

  useEffect(() => {
    if (menuOpen) {
      void refetch();
      return;
    }

    if (!menuOpen) {
      setPage(1);
      isFetchingRef.current = false;
    }
  }, [menuOpen, refetch]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const nearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 50;
    const totalPages = (listRes?.meta?.total_pages as number) || 1;
    const hasMore = page < totalPages;

    if (nearBottom && hasMore && !isFetchingRef.current) {
      isFetchingRef.current = true;
      setPage((prev) => prev + 1);
    }
  };

  return (
    <NotificationBellShell
      isSm={isSm}
      enabled
      anchor={anchor}
      setAnchor={setAnchor}
      menuOpen={menuOpen}
      unreadCount={unreadRes?.unread_count ?? 0}
      items={notificationList}
      listPending={listStatus === 'pending' && page === 1}
      isFetchingMore={isFetching}
      markRead={markRead}
      onMarkRead={(id) => {
        setNotificationList((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
      }}
      onMarkAllRead={() => {
        setNotificationList((prev) => prev.map((n) => ({ ...n, read: true })));
      }}
      markAllRead={markAllRead}
      onScroll={handleScroll}
    />
  );
}

function NotificationBellShell({
  isSm,
  enabled,
  anchor,
  setAnchor,
  menuOpen,
  unreadCount,
  items,
  listPending,
  isFetchingMore,
  markRead,
  onMarkRead,
  markAllRead,
  onMarkAllRead,
  onScroll
}: {
  isSm: boolean;
  enabled: boolean;
  anchor: null | HTMLElement;
  setAnchor: (v: null | HTMLElement) => void;
  menuOpen: boolean;
  unreadCount: number;
  items: InboxNotification[];
  listPending: boolean;
  isFetchingMore?: boolean;
  markRead: { mutate: (id: number) => void };
  onMarkRead?: (id: number) => void;
  markAllRead: { mutate: () => void; isPending: boolean };
  onMarkAllRead?: () => void;
  onScroll?: (e: React.UIEvent<HTMLDivElement>) => void;
}) {
  const onRowClick = (n: InboxNotification) => {
    if (!n.read) {
      markRead.mutate(n.id);
      onMarkRead?.(n.id);
    }
  };

  return (
    <>
      <IconButton
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-label='Notifications'
        sx={{ p: isSm ? 0.75 : 1 }}
        disabled={!enabled}
        size={isSm ? 'small' : 'medium'}
      >
        <Badge
          badgeContent={unreadCount > 99 ? '99+' : unreadCount}
          color='error'
          invisible={unreadCount === 0}
          sx={{
            '& .MuiBadge-badge': {
              fontSize: 10,
              minWidth: 18,
              height: 18,
              padding: '0 4px'
            }
          }}
        >
          <FiBell size={isSm ? 20 : 22} color='#09090A' />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              width: { xs: 'min(100vw - 24px, 380px)', sm: 380 },
              maxHeight: 480,
              mt: 1,
              borderRadius: 2,
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
            }
          }
        }}
      >
        <Box px={2} pt={1.5} pb={1} display='flex' alignItems='center' justifyContent='space-between' gap={1}>
          <Typography fontWeight={600} fontSize={16}>
            Notifications
          </Typography>
          <Button
            size='small'
            variant='text'
            onClick={() => {
              markAllRead.mutate();
              onMarkAllRead?.();
            }}
            disabled={unreadCount === 0 || markAllRead.isPending}
            sx={{ textTransform: 'none', fontSize: 13 }}
          >
            {markAllRead.isPending ? <CircularProgress size={16} /> : 'Mark all as read'}
          </Button>
        </Box>
        <Divider />
        <Box sx={{ maxHeight: 400, overflowY: 'auto', py: 0.5 }} onScroll={onScroll}>
          {menuOpen && listPending && (
            <Box display='flex' justifyContent='center' py={4}>
              <CircularProgress size={28} />
            </Box>
          )}
          {menuOpen && !listPending && items.length === 0 && (
            <Typography color='text.secondary' textAlign='center' py={4} px={2} fontSize={14}>
              You're all caught up.
            </Typography>
          )}
          {menuOpen &&
            !listPending &&
            items.map((n) => <NotificationRow key={n.id} n={n} onClick={() => onRowClick(n)} />)}
          {menuOpen && !listPending && items.length > 0 && isFetchingMore && (
            <Typography fontSize='small' color='secondary' mt={1} textAlign='center'>
              loading...
            </Typography>
          )}
        </Box>
      </Menu>
    </>
  );
}

export function NotificationRow({ n, onClick }: { n: InboxNotification; onClick: () => void }) {
  const unread = !n.read;
  const sourceLabel = n.badge_label?.trim() || '';
  const isSystemSource = sourceLabel.toLowerCase() === 'system';
  const relative = (() => {
    try {
      return formatDistanceToNow(new Date(n.created_at), { addSuffix: true });
    } catch {
      return '';
    }
  })();

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        gap: 1.5,
        px: 2,
        py: 1.25,
        cursor: 'pointer',
        bgcolor: unread ? 'rgba(37, 99, 235, 0.06)' : 'transparent',
        borderBottom: '1px solid #F0F0F0',
        '&:hover': { bgcolor: unread ? 'rgba(37, 99, 235, 0.09)' : '#FAFAFA' }
      }}
    >
      <NotificationRowIcon n={n} />
      <Box flex={1} minWidth={0}>
        <Typography
          fontSize={14}
          fontWeight={unread ? 600 : 400}
          color={unread ? '#09090A' : 'text.secondary'}
          sx={{ lineHeight: 1.35 }}
        >
          {n.title}
        </Typography>
        <Box display='flex' alignItems='center' gap={0.75} sx={{ mt: 0.75 }}>
          <Typography fontSize={12} color='text.disabled'>
            {relative}
          </Typography>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1,
              py: 0.5,
              borderRadius: 99,
              bgcolor: isSystemSource ? '#F5DFFF' : '#D2ECFF',
              color: isSystemSource ? '#7C00B1' : '#195EFF',
              width: 'fit-content'
            }}
          >
            {isSystemSource ? <ChatBotIcon /> : <FaRegUser size={10} />}
            <Typography fontSize={11} fontWeight={500} lineHeight={1.2} color='inherit'>
              {sourceLabel}
            </Typography>
          </Box>
        </Box>
      </Box>
      {unread ? (
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: 'primary.main',
            flexShrink: 0,
            mt: 0.75
          }}
        />
      ) : (
        <Box sx={{ width: 8, flexShrink: 0 }} />
      )}
    </Box>
  );
}
